import { useState, useEffect, useContext } from "react";
import { Toast as showToast } from "core/components/global/toast/toast";
import {
  getUsers,
  getGroups,
  updateUser,
} from "modules/agentMySecurity/services/embeddingsService";
import { User } from "../../admin.type";
import { SimpleTableContainer } from "../../admin.styled";
import { AuthContext } from "core/contexts/auth/auth";
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import AdminGroup from "./user";
import { Skeleton } from "@mui/material";
import { tableRowsLoader } from "../../home";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { CardHeader } from "../dashboard.styled";
import NoResultsFound from "core/components/global/tables/noResults/noResultsFound";

export default function AdminUsers() {
  const { amsUser } = useContext(AuthContext);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState<User[] | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' | null }>({
    key: '',
    direction: null
  });
  const [copyOfSearchValue, setCopyOfSearchValue] = useState("");

  useEffect(() => {
    async function fetchUsers() {
      try {
        const response = await getUsers();
        if (response === null) {
          setUsers([]);
          showToast("Unable to fetch users. You may not have the required permissions.", "error");
        } else {
          setUsers(response);
        }
        setUsersLoading(false);
      } catch (error) {
        setUsers([]);
        showToast("Error loading users data.", "error");
        setUsersLoading(false);
      }
    }

    async function fetchAdGroups() {
      try {
        const response = await getGroups();
        if (response === null) {
          setGroups([]);
          showToast("Unable to fetch groups. You may not have the required permissions.", "error");
        } else {
          const groupNames = response.map((group) => group.name);
          setGroups(groupNames);
        }
        setGroupsLoading(false);
      } catch (error) {
        setGroups([]);
        showToast("Error loading groups data.", "error");
        setGroupsLoading(false);
      }
    }

    fetchUsers();
    fetchAdGroups();
  }, []);

  // Remotely update the list of groups
  const handleUpdateUser = async (formData: User, userId: string) => {
    // Update the user's groups
    if (formData) {
      // Post the updated group
      const response = await updateUser(formData);

      if (response !== null) {
        // Notify the user of the successful change on the backend
        showToast(response.message, "success");

        // Update the user in the current state as a var
        const updatedUsers = users.map((user) =>
          user._id === formData.userId
            ? {
                ...user,
                ...formData,
              }
            : user
        );

        // Update the local users state to show in the UI
        setUsers(updatedUsers);
      }
    }
  };

  const handleFilterData = (newUsers, b, c) => {
    setFilteredUsers(newUsers);
  };

  const handleClearFilter = () => {
    setFilteredUsers(null);
  };

  // Sort functionality
  const requestSort = (key) => {
    let direction: 'ascending' | 'descending' | null = 'ascending';
    
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } else if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = null;
    }
    
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = () => {
    const dataToSort = filteredUsers || users;
    if (!sortConfig.key || sortConfig.direction === null) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    
    if (sortConfig.direction === 'ascending') return <ArrowUpwardIcon fontSize="small" />;
    if (sortConfig.direction === 'descending') return <ArrowDownwardIcon fontSize="small" />;
    return null;
  };

  const sortedData = getSortedData();

  const handleUsersDelete = (deletedKeyId) => {

    if (filteredUsers !== null) {
      setFilteredUsers(prevKeys => {
        if (prevKeys === null) return null;
        return prevKeys.filter(key => key._id !== deletedKeyId);
      });
    }
    setUsers(prevKeys => prevKeys.filter(key => key._id !== deletedKeyId));
  };

  return (
    <>
      <SimpleTableContainer elevation={1} columns={3}>
        {/* Data Search */}
        <div className="pb-3 d-flex justify-content-between align-items-center">
          <CardHeader className="p-2">All Users</CardHeader>
          <AdvanceSearch
            initialData={users}
            setFilteredData={handleFilterData}
            clearFilteredData={handleClearFilter}
            setCopyOfSearchValue={setCopyOfSearchValue}
          />
        </div>

        {/* Collapsable Table */}
        <div aria-label="collapsible table">
          {/* Headers */}
          <div
            style={{ borderRadius: "5px 5px 0 0" }}
            className="outer-header-container"
          >
            <div className="row-items">
              <p 
                className="header-item" 
                onClick={() => requestSort('username')}
                style={{ cursor: 'pointer' }}
              >
                Username {getSortIcon('username')}
              </p>
              <p 
                className="header-item" 
                onClick={() => requestSort('email')}
                style={{ cursor: 'pointer' }}
              >
                Email {getSortIcon('email')}
              </p>
              <p 
                className="header-item" 
                onClick={() => requestSort('createdAt')}
                style={{ cursor: 'pointer' }}
              >
                Created {getSortIcon('createdAt')}
              </p>
            </div>
            <div className="row-buttons"></div>
          </div>

          {/* Body */}
          <div
            style={{ borderRadius: "0 0 5px 5px" }}
            className="outer-row-container"
          >
            {(filteredUsers === null ? users : filteredUsers).length > 0 ? (

                filteredUsers === null ? (
                  <>
                    {users.map((user) => (
                      // Row items
                      <AdminGroup
                      key={`row-${user._id}`}
                      user={user}
                      amsUser={amsUser}
                      handleUpdateUser={handleUpdateUser}
                      availableGroups={groups}
                      onDelete={handleUsersDelete}
                      />
                    ))}
                  </>
                ) : (
                  <>
                    {filteredUsers.map((user) => (
                      // Row items
                      <AdminGroup
                      key={`row-${user._id}`}
                      user={user}
                      amsUser={amsUser}
                      handleUpdateUser={handleUpdateUser}
                      availableGroups={groups}
                      onDelete={handleUsersDelete}
                      />
                    ))}
                  </>
                )
            ) : (
              // If no results, show the NoResultsFound component
              <NoResultsFound
                searchTerm={filteredUsers !== null ? copyOfSearchValue : ""}
              />
            )}
          </div>
        </div>
      </SimpleTableContainer>
    </>
  );
}